import React, { useEffect } from 'react'

import { Box, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { Barcode } from 'src/components/Barcode'

import { convertGS1 } from './utils'

const useStyles = makeStyles({
  label: {
    width: '4in',
    height: '6in',
  },
  zoneA: {
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '1.25in',
  },
  zoneB: {
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2.75in',
    fontSize: '14px',
  },
  zoneC: {
    borderTop: '1px solid black',
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2.5in',
  },
  zoneD: {
    borderTop: '1px solid black',
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '1.5in',
    fontSize: '10px',
  },
  zoneE: {
    borderTop: '1px solid black',
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
  },
  zoneF: {
    borderTop: '1px solid black',
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
  },
  zoneG: {
    borderTop: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '4in',
  },
  zoneH: {
    borderTop: '1px solid black',
    height: '2in',
    width: '4in',
  },
  header: {
    fontSize: '12px',
    fontWeight: 'bold',
    paddingLeft: '3px',
  },
  address: {
    lineHeight: '1.0',
    paddingLeft: '10px',
  },
  from: {
    lineHeight: '1.0',
    fontSize: '10px',
    paddingLeft: '5px',
  },
  carrier: {
    fontWeight: 'bold',
    fontSize: '11px',
    paddingLeft: '5px',
  },
  to: {
    lineHeight: '1.0',
    fontWeight: 'bold',
    paddingLeft: '10px',
  },
  shipTo: {
    lineHeight: '1.0',
    fontSize: '12px',
    fontWeight: 'bold',
    paddingLeft: '6px',
  },
  container: {
    top: 'auto',
    bottom: 0,
  },
  center: {
    flexGrow: 1,
    textAlign: 'center',
  },
})

interface WalmartLabelProps {
  zoneA: string[]
  zoneB: string[]
  zoneC: string
  zoneD: {
    carrier: string
    pro: string
    bol: string
  }
  zoneE: {
    retailer_sku: string
    vendor_part_number: string
    count: number[]
  }
  zoneF: {
    store_id: string
    type: string
    department_number: string
    po: string
  }
  zoneG: string
  zoneH: string
}

interface Props {
  data: WalmartLabelProps
  onReadyToPrint?: () => void
}

export const WalmartLabel = (props: Props) => {
  const { data, onReadyToPrint } = props
  const ssccBarcode = { barcode: convertGS1(data.zoneH, 2), type: 'gs1-128', displayName: '' }
  const shipToBarcode = { barcode: convertGS1(data.zoneC, 3), type: 'gs1-128', displayName: '' }
  const classes = useStyles()

  useEffect(() => {
    onReadyToPrint?.()
  }, [onReadyToPrint])

  return (
    <div className={`always-page-break ${classes.label}`}>
      <div className="print-page">
        <Box className={classes.label}>
          <Grid container spacing={0}>
            <Grid item>
              <Box className={classes.zoneA}>
                <Typography className={classes.header}>FROM</Typography>
                {data.zoneA.map((line) => (
                  <Typography key={line} className={classes.from}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneB}>
                <Typography className={classes.header}>TO</Typography>
                {data.zoneB.map((line) => (
                  <Typography key={line} className={classes.to}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneC}>
                <Grid container>
                  <Grid item>
                    <Typography className={classes.header}>POSTAL CODE:</Typography>
                  </Grid>
                  <Grid item>
                    <Box display="flex" justifyContent="center" ml={4}>
                      <Typography variant="h6">{shipToBarcode.barcode}</Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box ml={6} mr={6}>
                  <Barcode barcode={shipToBarcode} scale={2} height={10} />
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneD}>
                <Typography className={classes.header}>CARRIER</Typography>
                <Typography className={classes.carrier}>{data.zoneD.carrier}</Typography>
                <Typography className={classes.carrier}>PRO #: {data.zoneD.pro}</Typography>
                <Typography className={classes.carrier}>B/L #: {data.zoneD.bol}</Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneE} pl={2}>
                  <Typography>WAL-MART Item #: {data.zoneE.retailer_sku}</Typography>
                  <Typography>Vendor item #: {data.zoneE.vendor_part_number}</Typography>
                  <Typography>CARTON: {data.zoneE.count[0]} of {data.zoneE.count[1]}</Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneF} pl={2}>
                  <Typography>DC #: {data.zoneF.store_id}</Typography>
                  <Typography>TYPE: {data.zoneF.type}</Typography>
                  <Typography>DEPT: {data.zoneF.department_number}</Typography>
                  <Typography>ORDER #: {data.zoneF.po}</Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneG} pl={2}>
                  <Typography variant="h5">VENDOR ITEM #: {data.zoneE.vendor_part_number}</Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box className={`${classes.zoneH} ${classes.container}`}>
                <Typography className={classes.header}>SSCC-18</Typography>
                <Box display="flex" justifyContent="center">
                  <Typography variant="h4">{ssccBarcode.barcode}</Typography>
                </Box>
                <Box ml={5} mr={5}>
                  <Barcode barcode={ssccBarcode} scale={2} height={20} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </div>
    </div>
  )
}
